import {
  DeliveryBand,
  DeliveryBandUpdate,
  EroadKey,
  Vehicle,
  VehicleDowntime,
  VehicleUpdate,
} from '@deliveries-app/models';
import api from '../../api';
import { IndexFilters } from '../models/booking-request.interface';
import {
  DeliveryBandsResponse,
  UpdateDeliveryBandResponse,
  UpdateVehicleResponse,
  VehicleDeliveryBandsResponse,
  VehicleResponse,
  VehiclesResponse,
} from '../models/driver-responses.interface';
import { processFilterParams } from '../utils/filterParams';

/**
 *  Get All Vehicles
 * @param {number} branch_id
 *
 * @returns {Promise<{data: Vehicle[]}>}
 */
export async function getVehiclesByBranch(
  branch_id: number
): Promise<{ data: Vehicle[] }> {
  try {
    const res = await api.get<VehiclesResponse>(
      `/api/v1/branches/${branch_id}/vehicles`
    );

    if (!res.status) {
      throw new Error('Failed to get all vehicles!');
    }

    return res.data;
  } catch (err) {
    console.log('Get all vehicles failed', err);
    throw err;
  }
}

/**
 *  Get All Vehicle Delivery Bands By Branch
 * @param {number} branch_id
 *
 * @returns {Promise<{data: DeliveryBand[]}>}
 */
export async function getVehicleDeliveryBandsByBranch(
  branch_id: number
): Promise<VehicleDeliveryBandsResponse> {
  try {
    const res = await api.get<VehicleDeliveryBandsResponse>(
      `/api/v1/branches/${branch_id}/vehicles/delivery-bands`
    );

    if (!res.data) {
      throw new Error('Failed to get all vehicle delivery bands by branch!');
    }

    return res.data;
  } catch (err) {
    console.log('Get all vehicle delivery bands by branch failed', err);
    throw err;
  }
}

/**
 *  Get All Vehicle Delivery Bands by Vehicle
 * @param {number} branch_id
 *
 * @returns {Promise<{data: DeliveryBand[]}>}
 */
export async function getVehicleDeliveryBands(
  branch_id: number,
  vehicle_id: number
): Promise<DeliveryBandsResponse> {
  try {
    const res = await api.get<DeliveryBandsResponse>(
      `/api/v1/branches/${branch_id}/vehicles/${vehicle_id}/delivery-bands`
    );

    if (!res.data) {
      throw new Error('Failed to get all vehicle delivery bands by vehicle!');
    }

    return res.data;
  } catch (err) {
    console.log('Get all vehicle delivery bands by vehicle failed', err);
    throw err;
  }
}

/**
 *  Get Vehicle By Id
 *
 * @param {number} branch_id
 * @param {number} vehicle_id
 *
 * @returns {Promise<{data: Vehicle}>}
 */
export async function getVehiclesById(
  vehicle_id: number,
  branch_id: number
): Promise<{ data: Vehicle }> {
  try {
    const res = await api.get<VehicleResponse>(
      `/api/v1/branches/${branch_id}/vehicles/${vehicle_id}`
    );

    if (!res.status) {
      throw new Error('Failed to get all vehicles!');
    }

    return res.data;
  } catch (err) {
    console.log('Get all vehicles failed', err);
    throw err;
  }
}

/**
 *  Post Vehicle Downtime
 *
 * @param {number} branch_id
 * @param {number} vehicle_id
 * @param {string} start_time
 * @param {string} end_time
 *
 * @returns {Promise<{data: Vehicle}>}
 */

export async function postVehicleDowntime(
  branch_id: number,
  vehicle_id: number,
  start_time: string,
  end_time: string
): Promise<{ success: boolean; data: VehicleDowntime[] }> {
  try {
    const res = await api.post(
      `/api/v1/branches/${branch_id}/vehicles/${vehicle_id}/downtime`,
      {
        start_time: start_time,
        end_time: end_time,
      }
    );

    return res.data;
  } catch (err) {
    console.log('Error post vehicle Downtime', err);
    throw err;
  }
}

/**
 *  Post Vehicle Downtime
 *
 * @param {number} branch_id
 * @param {number} vehicle
 *
 * @returns {Promise<{data: Vehicle}>}
 */

export async function getVehicleDowntime(
  branch_id: number,
  vehicle: number,
  filters?: IndexFilters
): Promise<VehicleDowntime[][]> {
  try {
    const params = processFilterParams({ filters: filters ?? {} });
    const res = await api.get<{ data: VehicleDowntime[][] }>(
      `/api/v1/branches/${branch_id}/vehicles/${vehicle}/downtime`,
      { params }
    );

    return res.data.data;
  } catch (err) {
    console.log('Get vehicle downtime error', err);
    throw err;
  }
}

/**
 *  Update Vehicle Delivery Band
 *
 * @param {number} branch_id
 * @param {number} vehicle_id
 * @param {number} id
 *
 * @returns {Promise<{data: DeliveryBand}>}
 */
export async function updateVehicleDeliveryBandById(
  branch_id: number,
  id: number,
  obj: DeliveryBandUpdate
): Promise<DeliveryBand> {
  try {
    const res = await api.put<UpdateDeliveryBandResponse>(
      `/api/v1/branches/${branch_id}/vehicles/delivery-bands/${id}`,
      obj
    );
    if (!res.data.success) {
      throw new Error('Update vehicle delivery band failed');
    }

    return res.data.data;
  } catch (err) {
    console.log('Update vehicle delivery band failed', err);
    throw err;
  }
}

/**
 *  Update Vehicle
 *
 * @param {number} branch_id
 * @param {number} id
 *
 * @returns {Promise<{data: Vehicle}>}
 */
export async function updateVehicleById(
  branch_id: number,
  id: number,
  obj: VehicleUpdate
): Promise<Vehicle> {
  try {
    const res = await api.put<UpdateVehicleResponse>(
      `/api/v1/branches/${branch_id}/vehicles/${id}`,
      obj
    );
    if (!res.data.success) {
      throw new Error('Create booking failed');
    }

    return res.data.data;
  } catch (err) {
    console.log('Submit vehicle Failed', err);
    throw err;
  }
}

/**
 *  Delete Vehicle Downtime
 * @param {number} branch_id
 * @param {number} vehicle
 * @param {number} downtime_id
 *
 * @returns {Promise<{data: Vehicle}>}
 */

export async function deleteDowntime(
  branch_id: number,
  vehicle: number,
  downtime: number
): Promise<{ success: boolean; data: VehicleDowntime[] }> {
  try {
    const res = await api.delete(
      `/api/v1/branches/${branch_id}/vehicles/${vehicle}/downtime/${downtime}`
    );
    return res.data;
  } catch (err) {
    console.log('Delete vehicle downtime error', err);
    throw err;
  }
}

/**
 *  Create Vehicle Delivery Band
 * @param {number} branch_id
 * @param {number} vehicle_id
 * @param {number} delivery_band_id
 *
 */
export async function createVehicleDeliveryBand(
  branch_id: number,
  vehicle_id: number,
  data: DeliveryBandUpdate
): Promise<EroadKey> {
  try {
    const res = await api.post(
      `/api/v1/branches/${branch_id}/vehicles/${vehicle_id}/delivery-bands`,
      data
    );

    return res.data;
  } catch (err) {
    console.log('Create vehicle delivery brand failed!', err);
    throw err;
  }
}

/**
 *  Delete Vehicle Delivery Band List
 * @param {number} branch_id
 * @param {number} vehicle
 * @param {number} delivery_band_id
 *
 */

export async function deleteVehicleDeliveryBand(
  branch_id: number,
  id: number
): Promise<DeliveryBand> {
  try {
    const res = await api.delete(
      `/api/v1/branches/${branch_id}/vehicles/delivery-bands/${id}`
    );
    return res.data;
  } catch (err) {
    console.log('Delete vehicle delivery band failed', err);
    throw err;
  }
}

/**
 *  Delete Vehicle List
 * @param {number} branch_id
 * @param {number} vehicle
 *
 */

export async function deleteVehicle(
  branch_id: number,
  id: number
): Promise<Vehicle> {
  try {
    const res = await api.delete(
      `/api/v1/branches/${branch_id}/vehicles/${id}`
    );
    return res.data;
  } catch (err) {
    console.log('Delete vehicle failed', err);
    throw err;
  }
}

export async function getEroads(id: number): Promise<EroadKey[]> {
  try {
    const res = await api.get(`/api/v1/branches/${id}/eroad`);
    return res.data.data;
  } catch (err) {
    console.log('Get eroad failed', err);
    throw err;
  }
}

export async function deleteEroadsById(id: number): Promise<EroadKey> {
  try {
    const res = await api.delete(`/api/v1/branches/${id}/eroad/${id}`);
    return res.data;
  } catch (err) {
    console.log('Delete eroad failed', err);
    throw err;
  }
}

export async function postEroads(
  branchId: number,
  apiKey: string,
  active: boolean
): Promise<EroadKey> {
  try {
    const res = await api.post(`/api/v1/branches/${branchId}/eroad/create`, {
      api_key: apiKey,
      active: active,
    });

    return res.data;
  } catch (err) {
    console.log('Post eroad failed', err);
    throw err;
  }
}

export async function getTimeStampBookingStatus(
  branch_id: number,
  vehicleId: number,
  startDate: string
): Promise<string> {
  try {
    const params = new URLSearchParams();
    params.append('start_date', startDate);

    const res = await api.get(
      `/api/v1/branches/${branch_id}/vehicles/${vehicleId}/delay`,
      {
        params,
      }
    );

    return res.data;
  } catch (err) {
    console.log('Error Get timestamp booking', err);
    throw err;
  }
}

export async function getBranchVehicleDowntimes(
  branch_id: number,
  startTime: string
): Promise<Array<VehicleDowntime[]>> {
  try {
    const params = new URLSearchParams();
    params.append('start_time', startTime);

    const res = await api.get(
      `/api/v1/branches/${branch_id}/vehicles/downtime`,
      { params }
    );

    return res.data.data;
  } catch (err) {
    console.log('Get vehicle downtime error', err);
    throw err;
  }
}

/**
 * Get all checklists for a vehicle
 */
export async function getVehicleChecklists(
  branch_id: number,
  vehicle_id: number
): Promise<any> {
  try {
    const res = await api.get(
      `/api/v1/branches/${branch_id}/vehicles/${vehicle_id}/checklists`
    );

    return res;
  } catch (err) {
    console.log('Error when get Vehicle', err);
    throw err;
  }
}

/**
 * Get a single vehicle checklist
 */
export async function getVehicleChecklistById(
  branch_id: number,
  vehicle_id: number,
  checklist_id: number
): Promise<any> {
  try {
    const res = await api.get(
      `/api/v1/branches/${branch_id}/vehicles/${vehicle_id}/checklists/${checklist_id}`
    );

    return res;
  } catch (err) {
    console.log('Error when get Vehicle', err);
    throw err;
  }
}

/**
 * Create a new vehicle checklist response
 */
export async function submitChecklistResponse(
  branch_id: number,
  vehicleId: string | number,
  response: any
): Promise<any> {
  try {
    const res = await api.post(
      `/api/v1/branches/${branch_id}/vehicles/${vehicleId}/checklists/actions/submit-checklist`,
      response
    );

    return res;
  } catch (err) {
    console.log('submit vehicle info request failed', err);
    throw err;
  }
}

export const vehicleService = {
  getVehicleDeliveryBandsByBranch,
  deleteVehicleDeliveryBand,
  updateVehicleDeliveryBandById,
  getVehiclesByBranch,
  deleteVehicle,
  updateVehicleById,
  getEroads,
  deleteEroadsById,
  postEroads,
  getTimeStampBookingStatus,
  postVehicleDowntime,
  getVehicleDowntime,
  getBranchVehicleDowntimes,
  deleteDowntime,
  getVehiclesById,
};
